import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Code, Factory, Wrench, Beaker, Utensils, Stethoscope, Zap, Car, Wheat,
  CheckCircle2, ArrowRight, Loader2, Shield, FileSearch, ClipboardList, Award, FileCheck,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { i: Code, t: "Software & Technology" },
  { i: Factory, t: "Manufacturing" },
  { i: Wrench, t: "Engineering / AEC" },
  { i: Beaker, t: "Life Sciences & Pharma" },
  { i: Utensils, t: "Food & Beverage" },
  { i: Stethoscope, t: "Healthcare & Med Devices" },
  { i: Zap, t: "Energy & Cleantech" },
  { i: Car, t: "Automotive & Aerospace" },
  { i: Wheat, t: "Agriculture & AgTech" },
];

const FOUR_PART = [
  { t: "Permitted Purpose", d: "The activity must be intended to create or improve a product, process, formula, technique, invention, or software — improving functionality, performance, reliability, or quality." },
  { t: "Technological in Nature", d: "The activity must rely on the principles of a hard science: engineering, computer science, biology, chemistry, or physics." },
  { t: "Elimination of Uncertainty", d: "At the start of the project, there must be technical uncertainty as to the capability, methodology, or appropriate design of the result." },
  { t: "Process of Experimentation", d: "The work must involve a systematic process of evaluating alternatives — modeling, simulation, trial and error, or scientific method." },
];

const PROCESS = [
  { i: FileSearch, t: "Free Preliminary Review", d: "We analyze your activities at no cost and confirm the credit opportunity is real before any engagement." },
  { i: ClipboardList, t: "Technical Activity Review", d: "We interview your engineers, developers, and technical staff to document every qualified project and uncertainty resolved." },
  { i: FileCheck, t: "QRE Calculation", d: "We calculate qualified research expenses: wages, supplies, contract research (65% rule), and cloud computing." },
  { i: Award, t: "Credit Delivery", d: "We deliver the federal credit calculation, state credit (where applicable), and form-ready figures to your CPA." },
  { i: Shield, t: "Audit Protection", d: "IRS-ready technical narratives, contemporaneous documentation, and full audit defense — included." },
];

const fmtUsd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function RDTaxCredit() {
  useSEO({
    title: "R&D Tax Credit Studies | Federal & State Credit Recovery | IncentTax",
    description:
      "Dollar-for-dollar federal R&D tax credit (plus most state credits) for businesses that design, build, improve, and engineer. 4-part test, QRE calculation, audit-ready documentation. Look back up to 3 prior tax years.",
    canonical: "https://incenttax.com/services/rd-tax-credit",
  });
  return (
    <PageShell testid="rd-page">
      {/* HERO */}
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">Service · 02</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            The R&D Tax Credit,
            <br /> <span className="italic text-brass">dollar for dollar.</span>
          </h1>
          <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            A federal (and most state) tax credit — not a deduction — for any
            business that designs, builds, improves, or engineers. Capture up
            to 3 prior open tax years.
          </p>

          <div className="mt-14 grid sm:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            <Stat k="6%–10%" v="of qualified expenses, typical credit" />
            <Stat k="3 Years" v="Open look-back window" />
            <Stat k="Permanent" v="Made permanent by 2015 PATH Act" />
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <a href="#rd-calculator" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-8 py-4 font-medium" data-testid="rd-cta-calculator">
              Estimate my credit <ArrowRight size={18} />
            </a>
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 border border-ivory/40 hover:bg-ivory hover:text-forest-900 px-8 py-4 font-medium transition-colors">
              Talk to an advisor
            </a>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">A brief history</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">
              Forty-three years of incentive. <span className="italic text-brass">Now permanent.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-charcoal text-lg leading-relaxed">
            <p>The R&D Tax Credit was created by the <strong>Economic Recovery Tax Act of 1981</strong> under President Reagan to keep U.S. innovation onshore.</p>
            <p>For decades it was a temporary provision — renewed by Congress <strong>16 separate times</strong> across multiple administrations.</p>
            <p>In December 2015, the <strong>PATH Act</strong> made the credit <strong>permanent</strong>, and expanded it to allow small businesses to apply the credit against payroll taxes — a game-changer for pre-revenue startups.</p>
            <p>Today it's one of the most under-claimed credits in the code: only ~33% of eligible businesses claim it.</p>
          </div>
        </div>
      </section>

      {/* WHAT THE CREDIT IS */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">What the credit actually is</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-8 max-w-4xl leading-tight">
            A credit, not a deduction — and that distinction is worth millions.
          </h2>
          <p className="text-charcoal text-lg leading-relaxed max-w-4xl mb-12">
            The credit allows businesses to offset their tax liability —
            <span className="italic"> dollar-for-dollar</span> — based on
            qualified research expenses (QREs).
          </p>

          <div className="grid md:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
            {[
              { t: "Dollar-for-Dollar", d: "A $100K credit reduces your tax liability by $100K. Not your taxable income — your tax bill directly." },
              { t: "Credit vs. Deduction", d: "A $100K deduction at a 30% rate saves $30K. A $100K credit saves $100K. The credit is ~3x more valuable." },
              { t: "Federal + State Stack", d: "The federal credit is just the start. Most states layer their own R&D credit on top — California, NY, Massachusetts, Texas, and many more." },
            ].map((c) => (
              <div key={c.t} className="bg-white p-10">
                <div className="font-serif text-3xl text-forest-900 mb-4">{c.t}</div>
                <p className="text-charcoal leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          {/* QRE COMPONENTS */}
          <div className="mt-20 grid lg:grid-cols-12 gap-12" data-testid="qre-components">
            <div className="lg:col-span-5">
              <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">The main components</p>
              <h3 className="font-serif text-3xl sm:text-4xl text-forest-900 leading-tight">
                Qualified Research Expenses <span className="italic text-brass">include:</span>
              </h3>
            </div>
            <ul className="lg:col-span-7 space-y-px bg-ivory-400 border border-ivory-400">
              {[
                { t: "Employee wages", d: "for time spent on R&D." },
                { t: "Supplies", d: "consumed during research." },
                { t: "Contract research", d: "65% of amounts paid to third parties." },
                { t: "Cloud computing costs", d: "used for R&D (added in recent years)." },
              ].map((q) => (
                <li key={q.t} className="bg-white p-6 sm:p-8 flex items-start gap-5">
                  <CheckCircle2 className="text-brass shrink-0 mt-1" size={22} strokeWidth={1.75} />
                  <div>
                    <div className="font-serif text-xl text-forest-900">{q.t}</div>
                    <p className="text-charcoal leading-relaxed mt-1">{q.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Who qualifies</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Far more industries qualify than people realize.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
            {INDUSTRIES.map(({ i: Icon, t }) => (
              <div key={t} className="bg-white p-8 hover:bg-ivory-100 transition-colors">
                <Icon className="text-brass mb-4" size={28} strokeWidth={1.5} />
                <div className="font-serif text-xl text-forest-900">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUR-PART TEST */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-brass uppercase tracking-[0.24em] text-sm mb-4">The four-part test</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-ivory mb-12 max-w-4xl leading-tight">
            Every qualifying activity passes <span className="italic text-brass">all four.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-ivory/10 border border-ivory/10">
            {FOUR_PART.map((f, i) => (
              <div key={f.t} className="bg-forest-900 p-10">
                <div className="font-serif text-5xl text-brass mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-serif text-2xl text-ivory mb-3">{f.t}</div>
                <p className="text-ivory/80 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <RDCalculator />

      {/* PROCESS */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Our process</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Five steps. <span className="italic text-brass">Audit-ready credit.</span>
          </h2>
          <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory-400 border border-ivory-400">
            {PROCESS.map(({ i: Icon, t, d }, idx) => (
              <li key={t} className="bg-white p-8">
                <Icon className="text-brass mb-4" size={28} strokeWidth={1.5} />
                <div className="font-serif text-4xl text-forest-700 mb-3">{String(idx + 1).padStart(2, "0")}</div>
                <div className="font-serif text-lg text-forest-900 mb-3">{t}</div>
                <p className="text-charcoal text-sm leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Shield className="text-brass mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-6">
            Free technical review. <span className="italic text-brass">No engagement until we confirm savings.</span>
          </h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
            A 30-minute call with one of our sales leaders — by the end you'll know whether your activities qualify and roughly how much credit is on the table.
          </p>
          <Link to="/#contact" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium" data-testid="rd-bottom-cta">
            Request Free Review <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bg-forest-900 p-8">
      <div className="font-sans text-3xl sm:text-4xl text-ivory tabular tracking-tight">{k}</div>
      <div className="text-ivory/60 text-xs uppercase tracking-[0.22em] mt-2">{v}</div>
    </div>
  );
}

export function RDCalculator() {
  const [form, setForm] = useState({
    industry: "Software & Technology",
    year_founded: new Date().getFullYear(),
    has_taken_before: false,
    current_year_wages: 0,
    current_year_supplies: 0,
    current_year_contract: 0,
    gross_receipts_2021: 0,
    gross_receipts_2022: 0,
    gross_receipts_2023: 0,
    gross_receipts_2024: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        year_founded: Number(form.year_founded),
        current_year_wages: Number(form.current_year_wages),
        current_year_supplies: Number(form.current_year_supplies),
        current_year_contract: Number(form.current_year_contract),
        gross_receipts_2021: Number(form.gross_receipts_2021),
        gross_receipts_2022: Number(form.gross_receipts_2022),
        gross_receipts_2023: Number(form.gross_receipts_2023),
        gross_receipts_2024: Number(form.gross_receipts_2024),
      };
      const { data } = await axios.post(`${API}/rd-credit-estimate`, payload);
      setResult(data);
    } catch (err) {
      toast.error("Could not calculate. Check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rd-calculator" data-testid="rd-calculator-section" className="py-24 bg-ivory-200">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">R&D credit calculator</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
          Plug in your numbers. <span className="italic text-brass">See the credit instantly.</span>
        </h2>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-px bg-ivory-400 border border-ivory-400">
          <div className="lg:col-span-7 bg-white p-8 sm:p-12">
            <h3 className="font-serif text-xl text-forest-900 mb-6">Company</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Industry">
                <select value={form.industry} onChange={(e) => u("industry", e.target.value)} data-testid="rd-industry" className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass">
                  {INDUSTRIES.map((x) => <option key={x.t}>{x.t}</option>)}
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Year Company Started">
                <input type="number" min="1900" max="2100" value={form.year_founded} onChange={(e) => u("year_founded", e.target.value)} data-testid="rd-year-founded" className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular" />
              </Field>
              <Field label="Have you claimed the R&D credit before?" className="sm:col-span-2">
                <div className="flex gap-3">
                  <Toggle on={form.has_taken_before} onClick={() => u("has_taken_before", true)} label="Yes" testid="rd-taken-yes" />
                  <Toggle on={!form.has_taken_before} onClick={() => u("has_taken_before", false)} label="No" testid="rd-taken-no" />
                </div>
              </Field>
            </div>

            <h3 className="font-serif text-xl text-forest-900 mt-10 mb-6">2025 Qualified Research Expenses</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Total Wages & Salaries (USD)">
                <input type="number" min="0" step="1000" value={form.current_year_wages} onChange={(e) => u("current_year_wages", e.target.value)} data-testid="rd-wages" className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular" />
              </Field>
              <Field label="Raw Materials & Supplies (USD)">
                <input type="number" min="0" step="1000" value={form.current_year_supplies} onChange={(e) => u("current_year_supplies", e.target.value)} data-testid="rd-supplies" className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular" />
              </Field>
              <Field label="Contract Research Costs (USD)" hint="65% IRS rule auto-applied">
                <input type="number" min="0" step="1000" value={form.current_year_contract} onChange={(e) => u("current_year_contract", e.target.value)} data-testid="rd-contract" className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular" />
              </Field>
            </div>

            <h3 className="font-serif text-xl text-forest-900 mt-10 mb-6">Gross Receipts — Prior Years</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[2021, 2022, 2023, 2024].map((y) => (
                <Field key={y} label={`${y} (USD)`}>
                  <input type="number" min="0" step="1000" value={form[`gross_receipts_${y}`]} onChange={(e) => u(`gross_receipts_${y}`, e.target.value)} data-testid={`rd-receipts-${y}`} className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular" />
                </Field>
              ))}
            </div>

            <button type="submit" disabled={loading} data-testid="rd-calculate" className="mt-8 inline-flex items-center justify-center gap-2 bg-forest-900 hover:bg-forest-800 text-ivory px-8 py-4 font-medium transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              Calculate My Credit
            </button>
          </div>

          {/* RESULT */}
          <div className="lg:col-span-5 bg-forest-900 text-ivory p-8 sm:p-12">
            <p className="text-brass uppercase tracking-[0.24em] text-xs mb-6">Estimated R&D credit</p>
            <ResultRow label="Wages" v={fmtUsd(result?.qre_wages)} testid="rd-result-wages" />
            <ResultRow label="Supplies" v={fmtUsd(result?.qre_supplies)} testid="rd-result-supplies" />
            <ResultRow label="Contract (65%)" v={fmtUsd(result?.qre_contract)} testid="rd-result-contract" />
            <ResultRow label="Total QREs" v={fmtUsd(result?.qre_total)} testid="rd-result-qre" bold />
            <ResultRow label="Method" v={result?.method ? result.method.toUpperCase() : "—"} testid="rd-result-method" />
            <ResultRow label="Base Amount" v={fmtUsd(result?.base_amount)} testid="rd-result-base" />
            <div className="mt-8 pt-8 border-t border-ivory/20">
              <p className="text-ivory/70 uppercase tracking-[0.22em] text-xs mb-3">Federal Credit</p>
              <div className="font-sans text-4xl text-ivory tabular" data-testid="rd-result-federal">{fmtUsd(result?.federal_credit)}</div>
              <p className="text-ivory/70 uppercase tracking-[0.22em] text-xs mt-6 mb-3">State Credit Estimate</p>
              <div className="font-sans text-2xl text-ivory/85 tabular" data-testid="rd-result-state">{fmtUsd(result?.state_credit_estimate)}</div>
              <p className="text-ivory/70 uppercase tracking-[0.22em] text-xs mt-6 mb-3">Total Estimated Credit</p>
              <div className="font-sans text-5xl sm:text-6xl text-brass tabular tracking-tight" data-testid="rd-result-total">{fmtUsd(result?.total_estimated_credit)}</div>
              <p className="text-ivory/60 text-sm mt-5 leading-relaxed">Estimate uses Alternative Simplified Credit (ASC) or Startup method as appropriate. A formal study refines this with actual prior-year QREs.</p>
              {result && (
                <a href="/#contact" data-testid="rd-claim-cta" className="mt-6 inline-flex items-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-6 py-3 text-sm font-medium transition-colors">
                  Claim this credit →
                </a>
              )}
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children, className = "", hint }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-forest-700 uppercase tracking-[0.2em] text-xs mb-3">{label}</span>
      {children}
      {hint && <span className="block text-charcoal-light text-xs mt-2">{hint}</span>}
    </label>
  );
}
function Toggle({ on, onClick, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`px-6 py-3 text-sm font-medium border transition-colors ${on ? "bg-forest-900 text-ivory border-forest-900" : "bg-white text-charcoal border-ivory-400 hover:border-forest-700"}`}>
      {label}
    </button>
  );
}
function ResultRow({ label, v, testid, bold }) {
  return (
    <div className={`flex items-baseline justify-between py-3 border-b border-ivory/10 ${bold ? "text-ivory" : "text-ivory/80"}`}>
      <span className="text-sm">{label}</span>
      <span className={`font-sans tabular ${bold ? "text-ivory text-xl" : "text-base"}`} data-testid={testid}>{v}</span>
    </div>
  );
}
